import React, { useEffect, useMemo, useState } from "react";
import { Row, Col, Card, Breadcrumb, Form } from "react-bootstrap";
import axios from "axios";
import config from "../../config";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { TabView, TabPanel } from "primereact/tabview";
import Draft_invoice from "../../components/Draft_invoice";
import Send_invoice from "../../components/Send_invoice";
import Cancel_invoice from "../../components/Cancel_invoice";
import Old_invoice from "../../components/Old_invoice";
import Recurring_Invoice from "../../components/Recurring_Invoice";

const Invoice_list = () => {
    const { accessToken } = useAuth();
    const navigate = useNavigate();

    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState("");
    const [selectedTaxTemplate, setSelectedTaxTemplate] = useState("");

    const [showInvoiceDialog, setShowInvoiceDialog] = useState(false);
    const [showTaxInvoiceDialog, setShowTaxInvoiceDialog] = useState(false);
    const [templateLoading, setTemplateLoading] = useState(false);
    const [templateError, setTemplateError] = useState("");

    useEffect(() => {
        const controller = new AbortController();

        const fetchTemplates = async () => {
            setTemplateLoading(true);
            setTemplateError("");

            try {
                const response = await axios.get(
                    `${config.apiBASEURL}/InvoiceRoutes/fetch-all-templates`,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                        },
                        signal: controller.signal,
                    }
                );

                setTemplates(
                    response.status === 200 && Array.isArray(response.data?.data)
                        ? response.data.data
                        : []
                );
            } catch (error) {
                if (error.code !== "ERR_CANCELED") {
                    console.error("Error fetching invoice templates:", error);
                    setTemplateError(
                        error.response?.data?.message ||
                        "Unable to fetch invoice templates."
                    );
                    setTemplates([]);
                }
            } finally {
                setTemplateLoading(false);
            }
        };

        if (accessToken) {
            fetchTemplates();
        }

        return () => controller.abort();
    }, [accessToken]);

    const taxInvoiceTemplates = useMemo(
        () =>
            templates.filter(
                (template) =>
                    String(template.template_name || "")
                        .trim()
                        .toLowerCase() === "tax invoice"
            ),
        [templates]
    );

    const openRegularInvoiceDialog = () => {
        setSelectedTemplate("");
        setShowInvoiceDialog(true);
    };

    const openTaxInvoiceDialog = () => {
        setSelectedTaxTemplate(
            taxInvoiceTemplates.length === 1
                ? String(taxInvoiceTemplates[0].template_id)
                : ""
        );
        setShowTaxInvoiceDialog(true);
    };

    const handleRegularTemplateSave = () => {
        if (!selectedTemplate) return;

        setShowInvoiceDialog(false);
        navigate(`/Graphe/dashboard/add_invoice/${selectedTemplate}`);
    };

    const handleTaxTemplateSave = () => {
        if (!selectedTaxTemplate) return;

        setShowTaxInvoiceDialog(false);
        navigate(
            `/Graphe/dashboard/create-tax-invoice/${selectedTaxTemplate}`
        );
    };

    const renderTemplateOptions = (items) => {
        if (templateLoading) {
            return <option disabled>Loading templates...</option>;
        }

        if (!items.length) {
            return <option disabled>No templates available</option>;
        }

        return items.map((template) => (
            <option
                key={template.template_id}
                value={template.template_id}
            >
                {template.template_name} / {template.invoice_prefix} /{" "}
                {template.financial_year}
            </option>
        ));
    };

    return (
        <>
            <Row className="body_content">
                <Row className="mx-0">
                    <Col
                        sm={4}
                        xs={12}
                        className="mb-4 d-flex justify-content-between align-items-center"
                    >
                        <Breadcrumb className="mb-0">
                            <Breadcrumb.Item active>
                                Invoice List
                            </Breadcrumb.Item>
                        </Breadcrumb>
                    </Col>

                    <Col
                        sm={8}
                        xs={12}
                        className="mb-4 d-flex justify-content-end align-items-center gap-2 flex-wrap"
                    >
                        <Button
                            label="Create Tax Invoice"
                            icon="pi pi-file-pdf"
                            severity="success"
                            onClick={openTaxInvoiceDialog}
                        />

                        <Button
                            label="Add Invoice"
                            icon="pi pi-plus"
                            severity="primary"
                            onClick={openRegularInvoiceDialog}
                        />
                    </Col>

                    <Card className="p-0">
                        <TabView>
                            <TabPanel
                                header="Draft"
                                leftIcon="pi pi-inbox mr-2"
                            >
                                <Draft_invoice />
                            </TabPanel>

                            <TabPanel
                                header="Recurring"
                                leftIcon="pi pi-history mr-2"
                            >
                                <Recurring_Invoice />
                            </TabPanel>

                            <TabPanel
                                header="Sent"
                                leftIcon="pi pi-send mr-2"
                            >
                                <Send_invoice />
                            </TabPanel>

                            <TabPanel
                                header="Downloading List"
                                leftIcon="pi pi-download mr-2"
                            >
                                <Cancel_invoice />
                            </TabPanel>

                            <TabPanel
                                header="Old Invoice"
                                leftIcon="pi pi-receipt mr-2"
                            >
                                <Old_invoice />
                            </TabPanel>
                        </TabView>
                    </Card>
                </Row>
            </Row>

            <Dialog
                header="Select Invoice Template"
                visible={showInvoiceDialog}
                style={{ width: "390px", maxWidth: "95vw" }}
                onHide={() => setShowInvoiceDialog(false)}
            >
                {templateError && (
                    <div className="alert alert-danger py-2">
                        {templateError}
                    </div>
                )}

                <Form.Group className="mb-3">
                    <Form.Label>Template</Form.Label>
                    <Form.Select
                        value={selectedTemplate}
                        onChange={(event) =>
                            setSelectedTemplate(event.target.value)
                        }
                        disabled={templateLoading}
                    >
                        <option value="" disabled>
                            Select Template
                        </option>
                        {renderTemplateOptions(templates)}
                    </Form.Select>
                </Form.Group>

                <div className="d-flex justify-content-end gap-2">
                    <Button
                        label="Cancel"
                        text
                        onClick={() => setShowInvoiceDialog(false)}
                    />
                    <Button
                        label="Select"
                        onClick={handleRegularTemplateSave}
                        disabled={!selectedTemplate}
                    />
                </div>
            </Dialog>

            <Dialog
                header="Create and Send Tax Invoice"
                visible={showTaxInvoiceDialog}
                style={{ width: "430px", maxWidth: "95vw" }}
                onHide={() => setShowTaxInvoiceDialog(false)}
            >
                <p className="text-muted mb-3">
                    Only templates whose name is exactly
                    <strong> Tax Invoice</strong> are shown here.
                </p>

                {templateError && (
                    <div className="alert alert-danger py-2">
                        {templateError}
                    </div>
                )}

                <Form.Group className="mb-3">
                    <Form.Label>Tax Invoice Template</Form.Label>
                    <Form.Select
                        value={selectedTaxTemplate}
                        onChange={(event) =>
                            setSelectedTaxTemplate(event.target.value)
                        }
                        disabled={templateLoading}
                    >
                        <option value="" disabled>
                            Select Tax Invoice Template
                        </option>
                        {renderTemplateOptions(taxInvoiceTemplates)}
                    </Form.Select>
                </Form.Group>

                {!templateLoading &&
                    taxInvoiceTemplates.length === 0 &&
                    !templateError && (
                        <div className="alert alert-warning py-2">
                            No template named “Tax Invoice” was found.
                        </div>
                    )}

                <div className="d-flex justify-content-end gap-2">
                    <Button
                        label="Cancel"
                        text
                        onClick={() => setShowTaxInvoiceDialog(false)}
                    />
                    <Button
                        label="Continue"
                        icon="pi pi-arrow-right"
                        iconPos="right"
                        severity="success"
                        onClick={handleTaxTemplateSave}
                        disabled={!selectedTaxTemplate}
                    />
                </div>
            </Dialog>
        </>
    );
};

export default Invoice_list;
